import React, { useEffect } from "react";

export default function EULA() {
  useEffect(() => {
    document.title = "End User License Agreement - Soundope";
  }, []);

  return (
    <div className="min-h-screen px-4 py-16">
      <div className="max-w-4xl mx-auto">
        <div className="neuro-base rounded-3xl p-8">
          <h1 className="text-3xl font-light text-[#d0d0d0] mb-6">End User License Agreement</h1>
          <p className="text-sm text-[#808080] mb-8">Last updated: November 03, 2025</p>

          <div className="space-y-6 text-sm text-[#909090] leading-relaxed">
            <p>
              This Agreement is provided to You (End-User) by Soundope, located at 6431 Old Hwy 99 S, Yreka, CA 96097 
              ("Licensor"), for use only under the terms of this License Agreement. By downloading the Licensed Application 
              from, and any update thereto (as permitted by this License Agreement), You indicate that You agree to be bound 
              by all of the terms and conditions of this License Agreement, and that You accept this License Agreement.
            </p>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">The Application</h2>
              <p>
                Soundope ("Licensed Application") is a piece of software created to facilitate music submission, feedback, 
                and discovery — and customized for mobile devices ("Devices"). It is used to allow artists to submit original 
                music for feedback and ranking, while supporters engage through listening, voting, and discovery.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Scope of License</h2>
              <p>
                You are given a non-transferable, non-exclusive, non-sublicensable license to install and use the Licensed 
                Application on any Devices that You (End-User) own or control and as permitted by the Usage Rules, with the 
                exception that such Licensed Application may be accessed and used by other accounts associated with You 
                (End-User, The Purchaser) via Family Sharing or volume purchasing.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Technical Requirements</h2>
              <p>
                Licensor attempts to keep the Licensed Application updated so that it complies with modified/new versions 
                of the firmware and new hardware. You are not granted rights to claim such an update. Licensor reserves the 
                right to modify the technical specifications as it sees appropriate at any time.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Maintenance and Support</h2>
              <p>
                The Licensor is solely responsible for providing any maintenance and support services for this Licensed 
                Application. You can reach the Licensor at the email address listed in the Overview for this Licensed 
                Application. Dead Entertainment and the End-User acknowledge that the Services have no obligation whatsoever 
                to furnish any maintenance and support services with respect to the Licensed Application.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Use of Data</h2>
              <p>
                You acknowledge that Licensor will be able to access and adjust Your downloaded Licensed Application content 
                and Your personal information, and that Licensor's use of such material and information is subject to Your 
                legal agreements with Licensor and Licensor's privacy policy.
              </p>
              <p className="mt-2">
                You acknowledge that the Licensor may periodically collect and use technical data and related information 
                about your device, system, and application software. Licensor may use this information to improve its 
                products or to provide services or technologies.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">User-Generated Contributions</h2>
              <p className="mb-3">
                The Licensed Application may provide you with the opportunity to create, submit, post, display, transmit, 
                perform, publish, distribute, or broadcast content and materials to us or on the Licensed Application, 
                including but not limited to text, writings, video, audio, photographs, graphics, comments, suggestions, 
                or personal information or other material (collectively, "Contributions").
              </p>
              <p className="mb-3">When you create or make available any Contributions, you thereby represent and warrant that:</p>
              <ul className="space-y-2 ml-6">
                <li>
                  The creation, distribution, transmission, public display, or performance, and the accessing, downloading, 
                  or copying of your Contributions do not and will not infringe the proprietary rights, including but not 
                  limited to the copyright, patent, trademark, trade secret, or moral rights of any third party.
                </li>
                <li>
                  You are the creator and owner of or have the necessary licenses, rights, consents, releases, and permissions 
                  to use and to authorize us, the Licensed Application, and other users of the Licensed Application to use 
                  your Contributions in any manner contemplated by the Licensed Application and this License Agreement.
                </li>
                <li>
                  You have the written consent, release, and/or permission of each and every identifiable individual person 
                  in your Contributions to use the name or likeness or each and every such identifiable individual person 
                  to enable inclusion and use of your Contributions in any manner contemplated by the Licensed Application 
                  and this License Agreement.
                </li>
                <li>Your Contributions are not false, inaccurate, or misleading.</li>
                <li>
                  Your Contributions are not unsolicited or unauthorized advertising, promotional materials, pyramid schemes, 
                  chain letters, spam, mass mailings, or other forms of solicitation.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Contribution License</h2>
              <p>
                You agree that we may access, store, process, and use any information and personal data that you provide 
                following the terms of the Privacy Policy and your choices (including settings). By submitting suggestions 
                or other feedback regarding the Licensed Application, you agree that we can use and share such feedback for 
                any purpose without compensation to you.
              </p>
              <p className="mt-2">
                We do not assert any ownership over your Contributions. You retain full ownership of all of your Contributions 
                and any intellectual property rights or other proprietary rights associated with your Contributions. We are 
                not liable for any statements or representations in your Contributions provided by you in any area on the 
                Licensed Application. You are solely responsible for your Contributions to the Licensed Application and you 
                expressly agree to exonerate us from any and all responsibility and to refrain from any legal action against 
                us regarding your Contributions.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Liability</h2>
              <p>
                Licensor's responsibility in the case of violation of obligations and tort shall be limited to intent and 
                gross negligence. Only in case of a breach of essential contractual duties (cardinal obligations), Licensor 
                shall also be liable in case of slight negligence. In any case, liability shall be limited to the foreseeable, 
                contractually typical damages. The limitation mentioned above does not apply to injuries to life, limb, or 
                health.
              </p>
              <p className="mt-2">
                Licensor takes no accountability or responsibility for any damages caused due to a breach of duties according 
                to Section 2 of this License Agreement. To avoid data loss, You are required to make use of backup functions 
                of the Licensed Application to the extent allowed by applicable third-party terms and conditions of use. You 
                are aware that in case of alterations or manipulations of the Licensed Application, You will not have access 
                to the Licensed Application.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Warranty</h2>
              <p>
                Licensor warrants that the Licensed Application is free of spyware, trojan horses, viruses, or any other 
                malware at the time of Your download. Licensor warrants that the Licensed Application works as described 
                in the user documentation. No warranty is provided for the Licensed Application that is not executable on 
                the device, that has been unauthorizedly modified, handled inappropriately or culpably, combined or installed 
                with inappropriate hardware or software, used with inappropriate accessories, regardless if by Yourself or by 
                third parties, or if there are any other reasons outside of Dead Entertainment's sphere of influence that 
                affect the executability of the Licensed Application.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Product Claims</h2>
              <p>
                Dead Entertainment and the End-User acknowledge that Dead Entertainment, and not the Services, is responsible 
                for addressing any claims of the End-User or any third party relating to the Licensed Application or the 
                End-User's possession and/or use of that Licensed Application, including, but not limited to:
              </p>
              <ul className="space-y-2 ml-6 mt-2">
                <li>(i) product liability claims;</li>
                <li>
                  (ii) any claim that the Licensed Application fails to conform to any applicable legal or regulatory 
                  requirement; and
                </li>
                <li>
                  (iii) claims arising under consumer protection, privacy, or similar legislation.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Legal Compliance</h2>
              <p>
                You represent and warrant that You are not located in a country that is subject to a U.S. Government embargo, 
                or that has been designated by the U.S. Government as a "terrorist supporting" country; and that You are not 
                listed on any U.S. Government list of prohibited or restricted parties.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Termination</h2>
              <p>
                The license is valid until terminated by Dead Entertainment or by You. Your rights under this license will 
                terminate automatically and without notice from Dead Entertainment if You fail to adhere to any term(s) of 
                this license. Upon License termination, You shall stop all use of the Licensed Application, and destroy all 
                copies, full or partial, of the Licensed Application.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Third-Party Terms of Agreements and Beneficiary</h2>
              <p>
                Dead Entertainment represents and warrants that Dead Entertainment will comply with applicable third-party 
                terms of agreement when using Licensed Application. In Accordance with Section 9 of the "Instructions for 
                Minimum Terms of Developer's End-User License Agreement," both Apple and Google and their subsidiaries shall 
                be third-party beneficiaries of this End User License Agreement and — upon Your acceptance of the terms and 
                conditions of this License Agreement, will have the right (and will be deemed to have accepted the right) to 
                enforce this End User License Agreement against You as a third-party beneficiary thereof.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Intellectual Property Rights</h2>
              <p>
                Dead Entertainment and the End-User acknowledge that, in the event of any third-party claim that the Licensed 
                Application or the End-User's possession and use of that Licensed Application infringes on the third party's 
                intellectual property rights, Dead Entertainment, and not the Services, will be solely responsible for the 
                investigation, defense, settlement, and discharge or any such intellectual property infringement claims.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Applicable Law</h2>
              <p>
                This License Agreement is governed by the laws of the State of California, excluding its conflicts of law rules.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Miscellaneous</h2>
              <p>
                If any of the terms of this agreement should be or become invalid, the validity of the remaining provisions 
                shall not be affected. Invalid terms will be replaced by valid ones formulated in a way that will achieve 
                the primary purpose. Collateral agreements, changes and amendments are only valid if laid down in writing. 
                The preceding clause can only be waived in writing.
              </p>
            </div>

            <div className="neuro-flat rounded-2xl p-6 mt-8">
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Contact Information</h2>
              <p className="mb-3">For general inquiries, complaints, questions or claims concerning the Licensed Application, please contact:</p>
              <div className="space-y-1 text-[#a0a0a0]">
                <p><strong>Dead Entertainment</strong></p>
                <p>6431 Old Hwy 99 S</p>
                <p>Yreka CA. 96097</p>
                <p>United States</p>
                <p className="mt-3">Email: jknoedler@soundope.com</p>
                <p>Phone: (+1)360-547-1912</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}